import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { strategicPillarService } from '../services/strategic-pillar'
import type { StrategicPillarFilterParams, PaginationParams, CreateStrategicPillar, UpdateStrategicPillar } from '../types'


export const strategicPillarKeys = {
  all: ['strategic-pillar'] as const,
  strategicPillars: () => [...strategicPillarKeys.all, 'strategic-pillars'] as const,
  strategicPillar: (id: string) => [...strategicPillarKeys.strategicPillars(), id] as const,
  strategicPillarsPaginated: (params: PaginationParams, filters: StrategicPillarFilterParams) =>
    [...strategicPillarKeys.strategicPillars(), 'paginated', params, filters] as const,
  stats: () => [...strategicPillarKeys.all, 'stats'] as const,
}


export const useStrategicPillars = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: StrategicPillarFilterParams = {}
) => {
  return useQuery({
    queryKey: strategicPillarKeys.strategicPillarsPaginated(params, filters),
    queryFn: () => strategicPillarService.getStrategicPillars(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}


export const useStrategicPillar = (strategicPillarId: string) => {
  return useQuery({
    queryKey: strategicPillarKeys.strategicPillar(strategicPillarId),
    queryFn: () => strategicPillarService.getStrategicPillarById(strategicPillarId),
    enabled: !!strategicPillarId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}



export const useStrategicPillarStats = () => {
  return useQuery({
    queryKey: strategicPillarKeys.stats(),
    queryFn: async () => {
      const [draftResponse, publishedResponse] = await Promise.all([
        strategicPillarService.getTotalDraftStrategicPillars(),
        strategicPillarService.getTotalPublishedStrategicPillars()
      ])

      return {
        draft: draftResponse.data || 0,
        published: publishedResponse.data || 0,
        total: (draftResponse.data || 0) + (publishedResponse.data || 0)
      }
    },
    staleTime: 5 * 60 * 1000
  })
}


export const useCreateStrategicPillar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateStrategicPillar) => strategicPillarService.createStrategicPillar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar creation failed:', error)
    }
  })
}


export const useUpdateStrategicPillar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ strategicPillarId, data }: { strategicPillarId: string; data: UpdateStrategicPillar }) =>
      strategicPillarService.updateStrategicPillar(strategicPillarId, data),
    onSuccess: (response, { strategicPillarId }) => {
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillar(strategicPillarId) })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      console.log(response)
    },
    onError: (error) => {
      console.error('Strategic pillar update failed:', error)
    }
  })
}


export const useUpdateStrategicPillarStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ strategicPillarId, status }: { strategicPillarId: string; status: 'DRAFT' | 'PUBLISH' }) =>
      strategicPillarService.updateStrategicPillarStatus(strategicPillarId, status),
    onSuccess: (response, { strategicPillarId }) => {
      if (response.success && response.data) {
        queryClient.setQueryData(strategicPillarKeys.strategicPillar(strategicPillarId), response.data)
      }
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar status update failed:', error)
    }
  })
}


export const useDeleteStrategicPillar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (strategicPillarId: string) => strategicPillarService.deleteStrategicPillar(strategicPillarId),
    onSuccess: (_, strategicPillarId) => {
      queryClient.removeQueries({ queryKey: strategicPillarKeys.strategicPillar(strategicPillarId) })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.strategicPillars() })
      queryClient.invalidateQueries({ queryKey: strategicPillarKeys.stats() })
    },
    onError: (error) => {
      console.error('Strategic pillar deletion failed:', error)
    }
  })
}
